import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Mail, Globe, CheckCircle } from 'lucide-react';
import { supabase } from '../config/supabase';
import { Skeleton } from '../components/ui/skeleton';
import Footer from '../components/Footer';
import SocialLinksList from '../components/SocialLinksList';

interface ProfessionalsCategory {
  id: number;
  name: string;
  category?: string | null;
}

interface ProfessionalBusiness {
  id: number;
  user_id?: string | null;
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  profession_id?: number | null;
  avatar_url?: string | null;
  website?: string | null;
  verified?: boolean | null;
  mockup?: boolean | null;
  slug?: string | null;
  professionals_categories?: ProfessionalsCategory | null;
  show_map?: boolean | null;
}

const ProfessionalDetailPage: React.FC = () => {
  const { slug: slugOrId } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<ProfessionalBusiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfessional = async () => {
      if (!slugOrId) {
        setError('No professional identifier provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        let businessData: any = null;
        let fetchedById = false;

        const slugQuery = await supabase
          .from('professionals')
          .select(`
            *,
            professionals_categories!inner(id, name, category)
          `)
          .eq('slug', slugOrId)
          .single();

        if (slugQuery.data) {
          businessData = slugQuery.data;
        } else {
          const maybeId = Number(slugOrId);
          if (!Number.isNaN(maybeId)) {
            const idQuery = await supabase
              .from('professionals')
              .select(`
                *,
                professionals_categories!inner(id, name, category)
              `)
              .eq('id', maybeId)
              .single();

            if (idQuery.data) {
              businessData = idQuery.data;
              fetchedById = true;
            }
          }
        }

        if (!businessData) {
          throw new Error('Professional business not found');
        }

        if (fetchedById && businessData.slug && businessData.slug !== slugOrId) {
          navigate(`/professional/${businessData.slug}`, { replace: true });
          return;
        }

        const transformed: ProfessionalBusiness = {
          ...businessData,
          professionals_categories: businessData.professionals_categories || null,
        };

        setBusiness(transformed);
      } catch (err) {
        console.error('Error fetching professional business:', err);
        setError('Failed to load professional details');
      } finally {
        setLoading(false);
      }
    };

    fetchProfessional();
  }, [slugOrId, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const hasLocation = Boolean(business?.location && business.location.trim().length > 0);
  const shouldShowMap = business ? (((business as any).show_map ?? true) as boolean) : false;
  const mapUrl = hasLocation && shouldShowMap && business
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${business.location}, Ireland`)}&output=embed`
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6 mt-16">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-6 w-32" />
                <div className="space-y-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
              <div className="flex justify-center">
                <Skeleton className="w-full max-w-sm aspect-square rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Professional Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error || "The professional you're looking for doesn't exist or may have been removed."}
            </p>
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 mt-16"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to results
        </button>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-8 space-y-8">
            <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:gap-10">
              {/* Business Name & Category */}
              <section className="space-y-3 lg:col-span-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {business.store_name || business.owner_name}
                  </h1>
                  {business.verified && (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  )}
                  {business.mockup && (
                    <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      Demo
                    </span>
                  )}
                </div>
                <p className="text-sm uppercase tracking-wide text-gray-500">
                  {business.professionals_categories?.name || 'Professional Service'}
                </p>
              </section>

              {/* Image / Avatar */}
              <section>
                <div className="w-full max-w-sm mx-auto aspect-square rounded-lg overflow-hidden bg-gray-100">
                  {business.avatar_url ? (
                    <img
                      src={business.avatar_url}
                      alt={business.store_name || business.owner_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <img
                        src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(business.store_name || business.owner_name)}&backgroundColor=f3f4f6&fontSize=40`}
                        alt={business.store_name || business.owner_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </section>

              {/* Contact Details */}
              <section className="space-y-4">
                {business.phone_number && (
                  <p className="flex items-center text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Phone:</span>
                    <a
                      href={`tel:${business.phone_number}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {business.phone_number}
                    </a>
                  </p>
                )}

                {business.email && (
                  <p className="flex items-center text-gray-700">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Email:</span>
                    <a
                      href={`mailto:${business.email}`}
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {business.email}
                    </a>
                  </p>
                )}

                {business.website && (
                  <p className="flex items-center text-gray-700">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Website:</span>
                    <a
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {business.website}
                    </a>
                  </p>
                )}

                <SocialLinksList userId={business.user_id || undefined} className="mt-2" />

                <p className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="font-medium mr-2">Location:</span>
                  {business.location}
                </p>
              </section>
            </div>

            {/* About Section */}
            <section className="pt-4 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {business.description || 'No description available.'}
              </p>
            </section>

            {/* Map Section */}
            {mapUrl && (
              <section className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Location Map</h3>
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <iframe
                    title={`Map for ${business.store_name || business.owner_name}`}
                    src={mapUrl}
                    width="100%"
                    height="260"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfessionalDetailPage;
